import type { GameConfig } from "./GameConfig";

/**
 * Persistência por nível. O Tweakpane grava o GameConfig inteiro
 * (bolhas, costura, vento, visual, áudio, score, …) só daquele id.
 * Disco: `src/levels/defaults/{id}.json`. Browser (só DEV): `onirya.defaults.{id}`.
 * Em produção o JSON entra no bundle — o último Save em DEV, no último build.
 */
export const DEFAULTS_STORAGE_KEY = "onirya.defaults";

const bundledDefaults = import.meta.glob("../levels/defaults/*.json", {
  eager: true,
  import: "default",
}) as Record<string, unknown>;

function bundledDefaultsFor(levelId: string): unknown | null {
  const suffix = `/${sanitizeLevelId(levelId)}.json`;
  for (const [file, data] of Object.entries(bundledDefaults)) {
    if (file.endsWith(suffix)) return data;
  }
  return null;
}

export function storageKeyForLevel(levelId: string): string {
  return `${DEFAULTS_STORAGE_KEY}.${sanitizeLevelId(levelId)}`;
}

export function sanitizeLevelId(id: string | null | undefined): string {
  const s = (id ?? "onirya").toLowerCase();
  return /^[a-z][a-z0-9-]{0,31}$/.test(s) ? s : "onirya";
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Copia valores guardados para o config vivo, só em chaves que já existem. */
export function deepMergeInto<T>(target: T, source: unknown): T {
  if (!isPlainObject(target) || !isPlainObject(source)) return target;
  const dest = target as Record<string, unknown>;
  for (const key of Object.keys(dest)) {
    if (!(key in source)) continue;
    const current = dest[key];
    const incoming = source[key];
    if (isPlainObject(current) && isPlainObject(incoming)) {
      deepMergeInto(current, incoming);
    } else if (Array.isArray(current) && Array.isArray(incoming)) {
      dest[key] = incoming.slice();
    } else if (incoming !== undefined && typeof incoming === typeof current) {
      dest[key] = incoming;
    }
  }
  return target;
}

export function snapshotConfig(config: GameConfig): GameConfig {
  return JSON.parse(JSON.stringify(config)) as GameConfig;
}

export function applyStoredDefaults(config: GameConfig, levelId: string): void {
  try {
    const id = sanitizeLevelId(levelId);
    const raw = localStorage.getItem(storageKeyForLevel(id));
    if (!raw) return;
    deepMergeInto(config, JSON.parse(raw));
  } catch {
    /* ignore corrupt storage */
  }
}

export async function saveConfigAsDefault(config: GameConfig, levelId: string): Promise<void> {
  const id = sanitizeLevelId(levelId);
  const snapshot = snapshotConfig(config);
  localStorage.setItem(storageKeyForLevel(id), JSON.stringify(snapshot));

  if (!import.meta.env.DEV) return;
  const response = await fetch(`/__onirya/save-defaults?level=${encodeURIComponent(id)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(snapshot, null, 2),
  });
  if (!response.ok) {
    throw new Error("Não foi possível escrever o ficheiro de defaults");
  }
}

/**
 * DEV: o JSON no disco manda (sem rebuild). Produção: o mesmo ficheiro,
 * baked no bundle. localStorage não sobrescreve o ficheiro em prod.
 */
export async function hydrateConfig(config: GameConfig, levelId: string): Promise<void> {
  const id = sanitizeLevelId(levelId);
  if (import.meta.env.DEV) {
    try {
      const response = await fetch(`/__onirya/defaults?level=${encodeURIComponent(id)}`, {
        cache: "no-store",
      });
      if (response.ok) {
        deepMergeInto(config, await response.json());
        localStorage.setItem(storageKeyForLevel(id), JSON.stringify(snapshotConfig(config)));
        return;
      }
    } catch {
      /* ficheiro indisponível: usa o JSON bundled ou o storage local */
    }
  }

  const shipped = bundledDefaultsFor(id);
  if (shipped) {
    deepMergeInto(config, shipped);
    return;
  }

  applyStoredDefaults(config, id);
}
